import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { Crosshair } from "@/components/Crosshair";
import {
  RefreshCw, Loader2, Info, Package, DollarSign, TrendingUp, TrendingDown, Tag, UserPlus,
  AlertTriangle, Briefcase, MessageSquarePlus, ScrollText, CheckCircle2, ChevronRight,
  X, ExternalLink, ClipboardCheck, ShoppingCart, GripVertical, RotateCcw,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, useSortable, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const fmtCents = (c) => (c === null || c === undefined ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(c / 100));
const fmtInt = (v) => (v === null || v === undefined ? "—" : v.toLocaleString("en-US"));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Client-side date-range label for the card hover tooltip (matches the backend window exactly).
function clientRange(metric) {
  const now = new Date();
  const f = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  if (metric === "gmv_mtd" || metric === "revenue_mtd") { const s = new Date(now.getFullYear(), now.getMonth(), 1); return `${f(s)} – ${f(now)}`; }
  if (metric === "revenue_7d") { const s = new Date(now); s.setDate(s.getDate() - 7); return `${f(s)} – ${f(now)}`; }
  if (metric === "active_listings" || metric === "total_listing_value") return "Current state (all time)";
  if (metric === "new_orders") return "Awaiting shipment · current";
  const s = new Date(now); s.setDate(s.getDate() - 30); return `${f(s)} – ${f(now)}`;
}

// Unified sparkline for every trend card: stroke-width 2, round caps, and an EMPHASIZED endpoint
// dot. Color is tied to trend direction (lime up / red down) — same treatment on all cards.
function Sparkline({ points, up, testid }) {
  if (!points || points.length < 2) return null;
  const w = 92, h = 30, pad = 4;
  const max = Math.max(...points), min = Math.min(...points);
  const range = max - min || 1;
  const iw = w - pad * 2, ih = h - pad * 2;
  const step = iw / (points.length - 1);
  const xy = points.map((v, i) => [pad + i * step, pad + ih - ((v - min) / range) * ih]);
  const d = xy.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [ex, ey] = xy[xy.length - 1];
  const stroke = up ? "#cfe86b" : "#e0736a";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible shrink-0" data-testid={testid} aria-hidden>
      <path d={d} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={ex} cy={ey} r="2.5" fill={stroke} />
    </svg>
  );
}

// One unified KPI card for all 8 metrics. THREE zones, identical on every card so they read as a
// single system: (1) Header — label left, metric icon top-right (the drag grip lives at the very
// corner via SortableKpiTile, clear of this icon); (2) Value — the hero number, Barlow Condensed
// 700, vertically centered; (3) Footer — a fixed-height row holding EITHER a WoW delta pill + the
// sparkline, OR the pending caption, OR a hover drill-in link. Variants: plain / sparkline / pending.
function KpiTile({ metricKey, label, tile, formatter, icon: Icon, testid, onOpen, spark, to }) {
  const pending = tile?.display_state === "stripe_gated";
  const warning = tile?.display_state === "warning";
  const hasVal = !pending && tile?.value !== null && tile?.value !== undefined;
  const value = hasVal ? formatter(tile.value) : "—";
  const HeaderIcon = pending ? Info : warning ? AlertTriangle : Icon;

  const collecting = !!spark && !pending && (!spark.points || spark.points.length < 2 || spark.state === "collecting");
  const sparkReady = !!spark && !pending && !collecting;
  const up = spark
    ? (spark.wow_pct != null ? spark.wow_pct >= 0 : (spark.points && spark.points[spark.points.length - 1] >= spark.points[0]))
    : true;

  const cls = `card ${pending ? "pending " : ""}relative flex flex-col h-full min-h-[140px] text-left bg-surface border border-sniper p-4 hover:border-olive transition-colors duration-200 group focus:outline-none focus-visible:border-olive-bright`;
  const inner = (
    <>
      {/* Zone 1 — header (pr-7 keeps the icon clear of the corner drag grip) */}
      <div className="flex items-start justify-between gap-2 pr-7">
        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-secondary-foreground font-semibold leading-tight line-clamp-2 min-h-[2.4em]">{label}</p>
        {HeaderIcon && (
          <HeaderIcon
            className={`w-4 h-4 shrink-0 mt-0.5 transition-colors ${pending ? "text-muted-foreground" : warning ? "text-[#E8C46A]" : "text-muted-foreground group-hover:text-olive-bright"}`}
            strokeWidth={1.8}
          />
        )}
      </div>

      {/* Zone 2 — the value, vertically centered and dominant. Type is pinned by the `.card-value`
          class (App.css): Barlow Condensed 700 @ 32px / #fff; `.card.pending .card-value` dims it. */}
      <div className="flex-1 flex flex-col justify-center">
        <p className="card-value tabular-nums" data-testid={`${testid}-value`} style={warning ? { color: "#E8C46A" } : undefined}>
          {value}
        </p>
      </div>

      {/* Zone 3 — fixed-height footer so all 8 cards bottom-align identically */}
      <div className="flex items-end justify-between gap-2 mt-3 h-8">
        {pending ? (
          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground" data-testid={`${testid}-note`}>
            <Info className="w-3.5 h-3.5 shrink-0" /> Activates with payments.
          </span>
        ) : warning ? (
          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#E8C46A]" data-testid={`${testid}-warning`} title={tile?.note || "This figure needs review before it's trusted."}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Check data source
          </span>
        ) : sparkReady ? (
          <>
            {spark.wow_pct != null ? (
              <span
                className={`inline-flex items-center gap-1 font-mono text-[10px] tabular-nums px-1.5 py-0.5 bg-black/40 ${up ? "text-olive-bright" : "text-blaze"}`}
                data-testid={`${testid}-wow`}
              >
                {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {Math.abs(spark.wow_pct)}% <span className="text-muted-foreground">WoW</span>
              </span>
            ) : <span aria-hidden />}
            <span className="ml-auto flex items-center justify-end" data-testid={`${testid}-spark`}>
              <Sparkline points={spark.points} up={up} testid={`${testid}-sparkline`} />
            </span>
          </>
        ) : collecting ? (
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground" data-testid={`${testid}-spark-collecting`}>Trend collecting…</span>
        ) : (
          <span className="inline-flex items-center gap-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground group-hover:text-olive-bright transition-colors">
            {to ? "View orders" : "Details"} <ChevronRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </>
  );
  // New Orders links straight to the (awaiting-shipment) Orders page; all other cards drill in.
  if (to) {
    return (
      <Link to={to} title="View awaiting-shipment orders" data-testid={testid} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(metricKey)} title={`Covers ${clientRange(metricKey)}`} data-testid={testid} className={cls}>
      {inner}
    </button>
  );
}

// KPI card catalog — the reorderable metric tiles. `id` doubles as the drilldown metric key.
// `tile`/`spark` read the right slice off the /kpis payload. DEFAULT_ORDER is the seed layout;
// each admin's saved order is reconciled against this (unknown ids dropped, new ids appended).
const KPI_DEFS = [
  { id: "new_orders", testid: "kpi-new-orders", label: "New Orders", icon: ShoppingCart, fmt: fmtInt, tile: (k) => k?.new_orders, spark: (k) => k?.new_orders_spark, to: "/admin/orders?status=awaiting" },
  { id: "active_listings", testid: "kpi-active-listings", label: "Active Listings", icon: Package, fmt: fmtInt, tile: (k) => k?.active_listings, spark: (k) => k?.active_listings_spark },
  { id: "total_listing_value", testid: "kpi-total-listing-value", label: "Total Listing Value", icon: Package, fmt: fmtCents, tile: (k) => k?.total_listing_value_cents, spark: (k) => k?.tlv_spark },
  { id: "gmv_mtd", testid: "kpi-gmv-mtd", label: "Gross Merchandise Value · Month to Date", icon: DollarSign, fmt: fmtCents, tile: (k) => k?.gmv_mtd_cents, spark: (k) => k?.gmv_spark },
  { id: "avg_sale_price", testid: "kpi-avg-sale", label: "Average Sale Price", icon: Tag, fmt: fmtCents, tile: (k) => k?.avg_sale_price_cents },
  { id: "revenue_mtd", testid: "kpi-revenue-mtd", label: "Revenue · Month to Date", icon: TrendingUp, fmt: fmtCents, tile: (k) => k?.revenue_mtd_cents },
  { id: "revenue_7d", testid: "kpi-revenue-7d", label: "Revenue · 7 Days", icon: TrendingUp, fmt: fmtCents, tile: (k) => k?.revenue_7d_cents },
  { id: "new_signups_30d", testid: "kpi-signups", label: "New Signups · 30 Days", icon: UserPlus, fmt: fmtInt, tile: (k) => k?.new_signups_30d, spark: (k) => k?.signups_spark },
];
const KPI_BY_ID = Object.fromEntries(KPI_DEFS.map((d) => [d.id, d]));
const DEFAULT_ORDER = KPI_DEFS.map((d) => d.id);

function reconcileOrder(saved) {
  const valid = (Array.isArray(saved) ? saved : []).filter((id) => KPI_BY_ID[id]);
  const missing = DEFAULT_ORDER.filter((id) => !valid.includes(id));
  return [...valid, ...missing];
}

// Wraps a KpiTile with a drag handle. The card itself stays a plain click-to-drill button;
// only the grip carries the drag listeners, so rearranging never fights the drill-in click.
function SortableKpiTile({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 30 : "auto" };
  return (
    <div ref={setNodeRef} style={style} className={`relative h-full ${isDragging ? "opacity-80" : ""}`} data-testid={`kpi-sortable-${id}`}>
      <span
        {...attributes}
        {...listeners}
        role="button"
        tabIndex={0}
        aria-label="Drag to rearrange card"
        title="Drag to rearrange"
        data-testid={`kpi-drag-${id}`}
        className="absolute top-2 right-2 z-20 p-1 -m-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-olive-bright touch-none focus:outline-none focus-visible:text-olive-bright"
      >
        <GripVertical className="w-3.5 h-3.5" strokeWidth={1.8} />
      </span>
      {children}
    </div>
  );
}

function DrillModal({ data, loading, onClose, fallbackLabel }) {
  const isRevenue = data?.metric === "revenue_mtd" || data?.metric === "revenue_7d";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="drill-modal" onClick={onClose}>
      <div className="bg-surface border border-sniper w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-sniper">
          <div>
            <h3 className="font-heading text-xl font-bold uppercase text-foreground" data-testid="drill-title">{data?.label || fallbackLabel || "Metric"}</h3>
            {data && <p className="font-mono text-[10px] uppercase text-muted-foreground mt-0.5">{data.date_range} · {data.excludes}</p>}
          </div>
          <button onClick={onClose} data-testid="drill-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-secondary-foreground font-mono text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading records…</div>
          ) : !data ? (
            <p className="text-muted-foreground font-mono text-sm py-8">No data.</p>
          ) : (
            <>
              {/* Headline total + reconciliation */}
              <div className="border border-sniper bg-obsidian p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">Reconciled total</span>
                  <span className="font-heading text-2xl font-bold text-olive-bright tabular-nums" data-testid="drill-total">{data.total_display}</span>
                </div>
                <p className="font-mono text-xs text-foreground mt-2" data-testid="drill-formula">{data.formula}</p>
                {data.statuses_counted && <p className="font-mono text-[11px] text-secondary-foreground mt-2">{data.statuses_counted}</p>}
                {data.note && <p className="font-mono text-[11px] text-volt mt-2 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {data.note}</p>}
                {data.reconciled === false && (
                  <p className="font-mono text-[11px] text-blaze mt-2 flex items-start gap-1.5" data-testid="drill-unreconciled"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {data.reason}</p>
                )}
              </div>

              {/* Revenue breakdown by source */}
              {isRevenue && (
                <div className="mt-4 border border-sniper divide-y divide-sniper" data-testid="drill-revenue-breakdown">
                  {data.breakdown.map((b) => (
                    <div key={b.source} className="px-3 py-2.5 flex items-center justify-between gap-3 font-mono text-xs">
                      <div className="min-w-0"><span className="text-foreground">{b.source}</span><div className="text-[10px] text-muted-foreground">{b.note}</div></div>
                      <span className={`tabular-nums shrink-0 ${b.cents === null ? "text-muted-foreground" : "text-foreground"}`}>{b.cents === null ? "—" : fmtCents(b.cents)}</span>
                    </div>
                  ))}
                  {data.memo && (
                    <div className="px-3 py-2.5 bg-amber-400/5" data-testid="drill-credit-memo">
                      <div className="flex items-center justify-between gap-3 font-mono text-xs">
                        <span className="text-amber-300">{data.memo.source}</span>
                        <span className="tabular-nums text-amber-300 shrink-0">{fmtCents(data.memo.cents)}</span>
                      </div>
                      <p className="font-mono text-[10px] text-amber-300/70 mt-1">{data.memo.note}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Record list */}
              {!isRevenue && (
                <>
                  <div className="flex items-center justify-between mt-4 mb-1">
                    <p className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="drill-count">{data.record_count} record{data.record_count === 1 ? "" : "s"}</p>
                    {data.link && <Link to={data.link} onClick={onClose} data-testid="drill-view-link" className="font-mono text-[10px] uppercase text-olive-bright hover:underline inline-flex items-center gap-1">Open full page <ExternalLink className="w-3 h-3" /></Link>}
                  </div>
                  {data.records.length === 0 ? (
                    <div className="border border-sniper p-6 text-center font-mono text-sm text-muted-foreground">No records in this window.</div>
                  ) : (
                    <div className="border border-sniper divide-y divide-sniper" data-testid="drill-records">
                      {data.records.map((r) => (
                        <div key={r.id} className="px-3 py-2 flex items-center justify-between gap-3 font-mono text-xs" data-testid={`drill-record-${r.id}`}>
                          <div className="min-w-0">
                            {r.link ? <Link to={r.link} target="_blank" className="text-foreground hover:text-volt truncate block max-w-[360px]">{r.title}</Link> : <span className="text-foreground truncate block max-w-[360px]">{r.title}</span>}
                            <span className="text-[10px] text-muted-foreground">{timeAgo(r.when)}</span>
                          </div>
                          {r.amount_cents !== undefined && r.amount_cents !== null && <span className="tabular-nums text-foreground shrink-0">{fmtCents(r.amount_cents)}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {data.records.length > 0 && (
                    <div className="border-x border-b border-sniper px-3 py-2 flex items-center justify-between font-mono text-xs bg-obsidian" data-testid="drill-footer-total">
                      <span className="uppercase text-muted-foreground">Footer total</span>
                      <span className="tabular-nums text-olive-bright font-bold">{data.total_display}</span>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const BUCKET_META = {
  reports: { icon: AlertTriangle, accent: "border-l-blaze", tone: "text-blaze" },
  content_review: { icon: ClipboardCheck, accent: "border-l-[#C2600F]", tone: "text-[#E8C46A]" },
  dealers: { icon: Briefcase, accent: "border-l-amber-400", tone: "text-amber-400" },
  tier_mismatch: { icon: DollarSign, accent: "border-l-blaze", tone: "text-blaze" },
  feedback: { icon: MessageSquarePlus, accent: "border-l-[#E8C46A]", tone: "text-[#E8C46A]" },
};

// EXPLICIT failed-to-load state — visually distinct from em-dash ("measured, nothing") and from
// an empty/all-clear panel. A panel that cannot load must SAY it could not load, with a retry.
function PanelError({ what, onRetry, testId }) {
  return (
    <div className="border border-blaze/50 bg-blaze/10 p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={testId}>
      <p className="font-mono text-sm text-blaze flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" /> Couldn&apos;t load {what}. This is a load error, not a zero.
      </p>
      <button onClick={onRetry} className="inline-flex items-center gap-1.5 border border-blaze/60 text-blaze hover:bg-blaze/15 font-mono uppercase text-xs px-3 py-1.5 transition-colors" data-testid={`${testId}-retry`}>
        <RefreshCw className="w-3.5 h-3.5" /> Retry
      </button>
    </div>
  );
}

function AttentionBucket({ bucket }) {
  const meta = BUCKET_META[bucket.key] || {};
  const Icon = meta.icon || AlertTriangle;
  const empty = bucket.count === 0;
  return (
    <div className={`border border-sniper bg-surface border-l-2 ${meta.accent || "border-l-sniper"}`} data-testid={`attention-bucket-${bucket.key}`}>
      <Link
        to={bucket.link}
        className="flex items-center justify-between gap-2 px-4 py-3 hover:bg-white/5 transition-colors"
        data-testid={`attention-link-${bucket.key}`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 shrink-0 ${empty ? "text-muted-foreground" : meta.tone}`} strokeWidth={1.8} />
          <span className="min-w-0">
            <span className="font-mono text-xs uppercase tracking-wide text-foreground truncate block">{bucket.label}</span>
            {!empty && bucket.oldest_at && (
              <span className="font-mono text-[10px] uppercase text-muted-foreground" data-testid={`attention-oldest-${bucket.key}`}>
                oldest waiting {timeAgo(bucket.oldest_at)}
              </span>
            )}
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span
            className={`font-heading text-lg font-bold tabular-nums ${empty ? "text-muted-foreground" : meta.tone}`}
            data-testid={`attention-count-${bucket.key}`}
          >
            {bucket.count}
          </span>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </span>
      </Link>
      <div className="border-t border-sniper/60">
        {empty ? (
          <p className="flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase text-muted-foreground" data-testid={`attention-clear-${bucket.key}`}>
            <CheckCircle2 className="w-3.5 h-3.5 text-olive-bright" /> All clear
          </p>
        ) : (
          <ul className="divide-y divide-sniper/50">
            {bucket.items.map((it) => (
              <li key={it.id} className="px-4 py-2.5" data-testid={`attention-item-${it.id}`}>
                <p className="text-sm font-body text-foreground line-clamp-1">{it.summary}</p>
                <span className="text-[10px] font-mono uppercase text-muted-foreground">{timeAgo(it.occurred_at)}</span>
              </li>
            ))}
            {bucket.count > bucket.items.length && (
              <li className="px-4 py-2">
                <Link to={bucket.link} className="text-[11px] font-mono uppercase text-olive-bright hover:underline" data-testid={`attention-more-${bucket.key}`}>
                  +{bucket.count - bucket.items.length} more →
                </Link>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function MembershipCapBanners() {
  const [alerts, setAlerts] = useState({ banners: [], derived: [] });
  const load = async () => {
    try { const { data } = await api.get("/admin/membership/alerts"); setAlerts(data || { banners: [], derived: [] }); }
    catch { /* cap module optional — never blank the dashboard */ }
  };
  useEffect(() => { load(); }, []);
  const dismiss = async (id) => { try { await api.post(`/admin/membership/alerts/${id}/dismiss`); load(); } catch {} };
  if (!alerts.banners?.length && !alerts.derived?.length) return null;
  return (
    <div className="mb-5 space-y-2" data-testid="dashboard-cap-banners">
      {alerts.banners.map((b) => (
        <div key={b.alert_id} data-testid={`dashboard-cap-banner-${b.alert_key}`} className="flex items-start justify-between gap-3 border border-[#C2600F]/50 bg-[#C2600F]/10 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#E8C46A] mt-0.5 shrink-0" />
            <div>
              <p className="font-heading uppercase text-sm tracking-tight text-[#E8C46A]">{b.title}</p>
              <p className="text-[13px] font-body text-secondary-foreground whitespace-pre-line mt-0.5">{b.body}</p>
              <Link to="/admin/membership-cap" className="inline-flex items-center gap-1 mt-1.5 font-mono text-[10px] uppercase text-olive-bright hover:underline">Open membership cap <ChevronRight className="w-3 h-3" /></Link>
            </div>
          </div>
          <button onClick={() => dismiss(b.alert_id)} data-testid={`dashboard-cap-dismiss-${b.alert_key}`} className="font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground border border-sniper px-2 py-1">Dismiss</button>
        </div>
      ))}
      {alerts.derived.map((d) => (
        <div key={d.key} data-testid={`dashboard-cap-flag-${d.key}`} className="flex items-start gap-2 border border-olive/40 bg-olive/5 px-4 py-3">
          <Info className="w-4 h-4 text-olive-bright mt-0.5 shrink-0" />
          <div>
            <p className="font-heading uppercase text-sm tracking-tight text-olive-bright">{d.title}</p>
            <p className="text-[13px] font-body text-secondary-foreground mt-0.5">{d.body}</p>
            <Link to="/admin/membership-cap" className="inline-flex items-center gap-1 mt-1.5 font-mono text-[10px] uppercase text-olive-bright hover:underline">Open membership cap <ChevronRight className="w-3 h-3" /></Link>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [attention, setAttention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpisError, setKpisError] = useState(false);
  const [attnError, setAttnError] = useState(false);
  const [drill, setDrill] = useState(null);
  const [drillMetric, setDrillMetric] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [order, setOrder] = useState(DEFAULT_ORDER);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const saveOrder = (next) => { api.put("/admin/dashboard/prefs", { kpi_order: next }).catch(() => {}); };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setOrder((cur) => {
      const next = arrayMove(cur, cur.indexOf(active.id), cur.indexOf(over.id));
      saveOrder(next);
      return next;
    });
  };

  const load = async (force = false) => {
    // allSettled so one failing panel never blanks the other, and a FAILED fetch is recorded as
    // an ERROR state — never left to paint em-dashes ("measured, nothing") over a request failure.
    // A 401 is handled globally by the axios interceptor (redirect to sign-in), so it won't land here.
    // Test/QA data is ALWAYS excluded — there is no reveal path anymore.
    const [k, a] = await Promise.allSettled([
      api.get(`/admin/analytics/kpis${force ? "?_force_refresh=1" : ""}`),
      api.get(`/admin/dashboard/attention`),
    ]);
    if (k.status === "fulfilled") { setKpis(k.value.data); setKpisError(false); }
    else { setKpisError(true); }
    if (a.status === "fulfilled") { setAttention(a.value.data); setAttnError(false); }
    else { setAttnError(true); }
    setLoading(false); setRefreshing(false);
  };

  useEffect(() => {
    load();
    // Restore this admin's saved card order (server-side, per account → follows across devices).
    api.get("/admin/dashboard/prefs")
      .then(({ data }) => setOrder(reconcileOrder(data?.kpi_order)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => { setRefreshing(true); load(true); };

  // Reset Layout — snap the KPI cards back to their default order and clear the saved per-admin
  // layout (persisting an empty array; reconcileOrder([]) rehydrates to DEFAULT_ORDER next visit).
  const resetLayout = () => { setOrder(DEFAULT_ORDER); saveOrder([]); };

  const openDrill = async (metric) => {
    setDrillMetric(metric); setDrill(null); setDrillLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics/kpis/drilldown?metric=${metric}`);
      setDrill(data);
    } catch { setDrill(null); }
    finally { setDrillLoading(false); }
  };
  const closeDrill = () => { setDrillMetric(null); setDrill(null); };

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-6 text-foreground" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="admin-dashboard-title">Dashboard</h2>
          <p className="text-xs font-mono uppercase text-muted-foreground mt-1">Platform metrics &amp; what needs your attention · click any card to see the rows behind it · drag the grip to rearrange</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetLayout} data-testid="dashboard-reset-layout" title="Reset the cards to their default order" className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-sm px-4 py-2.5 transition-colors">
            <RotateCcw className="w-4 h-4" /> Reset Layout
          </button>
          <button onClick={refresh} data-testid="dashboard-refresh" className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm px-4 py-2.5 transition-colors">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <Crosshair className="my-6" />

      <MembershipCapBanners />

      {loading ? (
        <div className="flex items-center gap-2 text-secondary-foreground font-mono text-sm" data-testid="dashboard-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading metrics…
        </div>
      ) : (
        <>
          {kpisError && !kpis ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-kpis">
              <div className="col-span-2 lg:col-span-4">
                <PanelError what="platform metrics" onRetry={refresh} testId="kpis-error" />
              </div>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={order} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-fr gap-3" data-testid="dashboard-kpis">
                  {order.map((id) => {
                    const def = KPI_BY_ID[id];
                    if (!def) return null;
                    return (
                      <SortableKpiTile key={id} id={id}>
                        <KpiTile
                          metricKey={def.id}
                          testid={def.testid}
                          label={def.label}
                          tile={def.tile(kpis)}
                          formatter={def.fmt}
                          icon={def.icon}
                          onOpen={openDrill}
                          spark={def.spark ? def.spark(kpis) : undefined}
                          to={def.to}
                        />
                      </SortableKpiTile>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <div className="flex items-center justify-between mt-10 mb-3">
            <h3 className="font-heading text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
              Needs Attention
              {attention?.total > 0 && (
                <span className="font-mono text-xs bg-blaze text-black px-2 py-0.5" data-testid="attention-total">{attention.total}</span>
              )}
            </h3>
            <Link to="/admin/audit-events" data-testid="dashboard-audit-link" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-secondary-foreground hover:text-foreground transition-colors">
              <ScrollText className="w-4 h-4" /> Full activity stream
            </Link>
          </div>

          {attnError && !attention ? (
            <PanelError what="the attention queue" onRetry={refresh} testId="attention-error" />
          ) : attention?.total === 0 ? (
            <div className="border border-sniper bg-surface p-8 flex flex-col items-center text-center gap-2" data-testid="attention-all-clear">
              <CheckCircle2 className="w-8 h-8 text-olive-bright" strokeWidth={1.5} />
              <p className="font-heading text-lg uppercase tracking-tight">Nothing needs attention</p>
              <p className="font-body text-sm text-muted-foreground">No open reports, pending business applications, or unresolved feedback.</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-3" data-testid="attention-grid">
              {(attention?.buckets || []).map((b) => <AttentionBucket key={b.key} bucket={b} />)}
            </div>
          )}
        </>
      )}

      {drillMetric && <DrillModal data={drill} loading={drillLoading} onClose={closeDrill} fallbackLabel={KPI_BY_ID[drillMetric]?.label} />}
    </div>
  );
}
